import Copicake from "@copicake/copicake-js";
import { toast } from "react-toastify";
import { useContext, useState } from "react";
import { ObjectChange } from "@eragonj/copicake-fabric/dist/types/utils";
import Change from "@components/Playground/Change";
import Sidebar from "@components/Sidebar";
import { AppContext } from "../context/AppContext";
import { KeyIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

const DEFAULT_PLACEHOLDER = "__DEFAULT__";
const PLACEHOLDER_IMAGE_URL = `${process.env.PUBLIC_URL}/placeholder.png`;

const defaultTextChange: ObjectChange = {
  name: "",
  text: DEFAULT_PLACEHOLDER,
  fill: DEFAULT_PLACEHOLDER,
};

const defaultImageChange: ObjectChange = {
  name: "",
  src: DEFAULT_PLACEHOLDER,
};

function Playground() {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  const [isCreating, setIsCreating] = useState(false);
  const [apiKey, setApiKey] = useState(user?.api_key || "");
  const [imageUrl, setImageUrl] = useState(PLACEHOLDER_IMAGE_URL);
  const [templateId, setTemplateId] = useState("");
  const [changes, setChanges] = useState<ObjectChange[]>([
    defaultTextChange,
    defaultImageChange,
  ]);

  function areChangesValid(changes: ObjectChange[]): boolean {
    return changes.every((change) => {
      return change.name && change.name.length > 0;
    });
  }

  function getFilteredChanges(changes: ObjectChange[]): ObjectChange[] {
    return changes.map((change) => {
      const cloned = { ...change };

      if ("text" in cloned && cloned.text === DEFAULT_PLACEHOLDER) {
        delete cloned.text;
      }

      if ("fill" in cloned && cloned.fill === DEFAULT_PLACEHOLDER) {
        delete cloned.fill;
      }

      if ("src" in cloned && cloned.src === DEFAULT_PLACEHOLDER) {
        delete cloned.src;
      }

      return cloned;
    });
  }

  async function onSubmit() {
    if (isCreating) {
      return;
    }

    if (!apiKey) {
      toast.error("Please enter your API key");
      return;
    }

    if (!templateId) {
      toast.error("Please enter your template id");
      return;
    }

    if (!areChangesValid(changes)) {
      toast.error("Please fill required parameters");
      return;
    }

    setIsCreating(true);

    const options = {
      template_id: templateId,
      changes: getFilteredChanges(changes),
    };

    const toastId = toast.loading("Creating image...");

    try {
      const copicake = new Copicake({ apiKey });
      const rendering = await copicake.image.create(options);
      toast.update(toastId, { render: "Retrieving image..." });

      const finalRendering = await copicake.image.getUntilFinished(
        rendering.id
      );

      if (finalRendering.status === "success") {
        setImageUrl(finalRendering.permanent_url);
        toast.update(toastId, {
          render: "Image created!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      } else {
        throw new Error("failed to render");
      }
    } catch (error) {
      console.error(error);
      toast.update(toastId, {
        render: (error as Error).message,
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }

    setIsCreating(false);
  }

  return (
    <div className="flex h-full flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="playground" />
        <main className="flex-1 overflow-y-auto px-8 py-10">
          <div className="mb-8">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
              <div>
                <h1 className="mb-2 text-3xl font-extrabold tracking-tight text-gray-900">
                  {t("playground_title")}
                </h1>
                <p className="text-lg text-gray-600">
                  {t("playground_intro_1")}
                </p>
                <p className="text-lg text-gray-600">
                  {t("playground_intro_2")}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setChanges((prevChanges) => {
                      return [...prevChanges, defaultTextChange];
                    });
                  }}
                  className="focus:shadow-outline transform rounded-lg border border-gray-300 bg-gray-200 py-2 px-4 font-medium text-gray-800 transition-all duration-200 hover:scale-105 hover:bg-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  disabled={isCreating}
                >
                  {t("playground_add_text_change")}
                </button>
                <button
                  onClick={() => {
                    setChanges((prevChanges) => {
                      return [...prevChanges, defaultImageChange];
                    });
                  }}
                  className="focus:shadow-outline transform rounded-lg border border-gray-300 bg-gray-200 py-2 px-4 font-medium text-gray-800 transition-all duration-200 hover:scale-105 hover:bg-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  disabled={isCreating}
                >
                  {t("playground_add_image_change")}
                </button>
                <button
                  className="focus:shadow-outline transform rounded-lg bg-gradient-to-r from-gray-900 to-gray-800 py-2 px-6 font-bold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  onClick={onSubmit}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <span className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>{t("playground_rendering")}</span>
                    </span>
                  ) : (
                    t("playground_render_button")
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-start justify-around gap-8">
            <div className="min-w-0 flex-1">
              <div className="space-y-6">
                <div>
                  <label className="text-md mb-2 block font-bold text-gray-700">
                    <span>{t("playground_api_token")}</span>
                    <span className="px-2 text-xs text-red-500">
                      {t("common_required")}
                    </span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <KeyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      className="focus:shadow-outline w-full appearance-none rounded-lg border border-gray-300 py-3 pl-10 pr-4 leading-tight text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      type="text"
                      placeholder={t("playground_api_key_placeholder")}
                      value={apiKey}
                      onChange={(e) => {
                        setApiKey(e.target.value);
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-md mb-2 block font-bold text-gray-700">
                    <span>{t("playground_template_id")}</span>
                    <span className="px-2 text-xs text-red-500">
                      {t("common_required")}
                    </span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      className="focus:shadow-outline w-full appearance-none rounded-lg border border-gray-300 py-3 pl-10 pr-4 leading-tight text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      type="text"
                      placeholder={t("playground_template_id_placeholder")}
                      value={templateId}
                      onChange={(e) => {
                        setTemplateId(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {changes.map((change, index) => {
                  return (
                    <Change
                      key={index}
                      change={change}
                      onValueUpdated={(newChange) => {
                        const newChanges = [...changes];
                        newChanges[index] = newChange;
                        setChanges(newChanges);
                      }}
                      onRemove={() => {
                        const newChanges = [...changes];
                        newChanges.splice(index, 1);
                        setChanges(newChanges);
                      }}
                    />
                  );
                })}

                <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                        <svg
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="text-sm text-blue-800">
                        <span className="font-semibold">
                          {t("playground_note_1_label")}
                        </span>{" "}
                        {t("playground_note_1_body")}
                      </div>
                      <div className="text-sm text-blue-800">
                        <span className="font-semibold">
                          {t("playground_note_2_label")}
                        </span>{" "}
                        {t("playground_note_2_body")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative flex h-96 w-96 flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white shadow-xl backdrop-blur-sm">
              <div className="h-3/4 w-3/4">
                {imageUrl === PLACEHOLDER_IMAGE_URL ? (
                  <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                    <div className="text-center">
                      <div className="mx-auto mb-2 h-12 w-12 text-gray-400">
                        <svg
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500">
                        {t("playground_preview_placeholder")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    className="h-full w-full rounded-lg bg-contain bg-center bg-no-repeat shadow-inner"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  ></div>
                )}
              </div>
              <div className="absolute top-4 right-4 rounded-full border border-gray-200 bg-white/90 py-2 px-4 text-sm font-semibold shadow-lg backdrop-blur-sm">
                <span className="flex items-center space-x-2">
                  <span>{t("playground_live_preview")}</span>
                  <span className="inline-block h-2 w-2 rounded-full border border-white bg-green-500 align-middle"></span>
                </span>
              </div>
              {isCreating && (
                <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm">
                  <div className="text-center">
                    <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
                    <p className="text-sm text-gray-600">
                      {t("playground_rendering")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Playground;
